import { useCallback, useState } from 'react';
import type { ClientRoomState, PlaylistSummary } from '@hitster/shared';
import { MIN_PLAYABLE_TRACKS } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { getSocket } from '../net/socket.ts';
import type { RoomApi } from '../net/useRoom.ts';
import { Alert, Progress, Spinner } from './common.tsx';
import { IconReplay } from './Icon.tsx';

/**
 * Playlist-Auswahl fuer den Host.
 *
 * Zwei Wege: ein Spotify-Link, oder die eigenen Playlists aus dem Konto.
 *
 * Der Link steht bewusst vorn und das Auflisten passiert nur auf Klick.
 * Grund ist Spotifys Kontingent: die eigene Playlist-Liste kostet wegen
 * der Paginierung bis zu zehn Anfragen, ein Link genau eine. Wer weiss,
 * welche Playlist er will, soll nicht fuer das Blaettern bezahlen.
 *
 * Fremde Playlists bleiben im Development Mode gesperrt -- daran aendert
 * auch ein direkter Link nichts, die Sperre haengt am Besitzverhaeltnis.
 */
export function PlaylistPicker({
  state,
  api,
}: {
  state: ClientRoomState;
  api: RoomApi;
}) {
  const t = useT();
  const [playlists, setPlaylists] = useState<PlaylistSummary[] | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [pasted, setPasted] = useState('');
  const [busyRef, setBusyRef] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  /**
   * `force` nur beim ausdruecklichen Aktualisieren.
   *
   * Der Abruf kostet serverseitig bis zu zehn Spotify-Anfragen. Sonst
   * genuegt der Zwischenspeicher -- andernfalls laeuft das Kontingent
   * voll, bevor eine Runde beginnt.
   */
  const loadPlaylists = useCallback((force = false) => {
    setLoadingList(true);
    setListError(null);

    getSocket().emit('host:playlists', { force }, (res) => {
      setLoadingList(false);
      if (res.ok) setPlaylists(res.data);
      else setListError(res.error.code);
    });
  }, []);

  const select = async (ref: string) => {
    setBusyRef(ref);
    try {
      await api.selectPlaylist(ref);
    } finally {
      // MUSS immer zurueckgesetzt werden. Bleibt `busyRef` haengen, sind
      // alle Playlists dauerhaft deaktiviert -- Klicks loesen dann gar
      // nichts mehr aus, ohne jeden Hinweis auf die Ursache.
      setBusyRef(null);
    }
  };

  const selected = state.playlist;
  const progress = selected?.progress;
  const tooFew = selected !== null && selected.usableTrackCount < MIN_PLAYABLE_TRACKS;

  return (
    <div className="panel panel--solid">
      <div className="panel__title">{t('playlist.title')}</div>

      {/* --- Aktuelle Auswahl --- */}
      {selected && (
        <div className="stack stack--tight" style={{ marginBottom: '1rem' }}>
          <div className="playlist-item playlist-item--selected" style={{ cursor: 'default' }}>
            {selected.imageUrl && (
              <img className="playlist-item__cover" src={selected.imageUrl} alt="" />
            )}
            <div className="min-w-0" style={{ flex: 1 }}>
              <div className="playlist-item__name">{selected.name}</div>
              <div className="playlist-item__meta">
                {t('playlist.usable', {
                  usable: selected.usableTrackCount,
                  total: selected.totalTrackCount,
                })}
              </div>
            </div>
          </div>

          {tooFew && (
            <Alert kind="error">{t('lobby.needTracks', { min: MIN_PLAYABLE_TRACKS })}</Alert>
          )}

          {/* Der Host muss wissen, dass hier nicht alle Songs drin sind --
              sonst wundert er sich ueber ein zu kleines Deck. */}
          {selected.limitedAccess && (
            <Alert kind="warning">{t('playlist.limitedAccess')}</Alert>
          )}

          {/* Aus dem Zwischenspeicher, weil Spotify gesperrt hat. Das
              Spiel laeuft -- aber gerade hinzugefuegte Songs fehlen. */}
          {selected.stale && (
            <Alert kind="warning">
              {t('playlist.staleCache', { minutes: selected.staleAgeMinutes ?? 0 })}
            </Alert>
          )}

          {progress && (
            <div className="stack stack--tight">
              <Progress value={progress.resolved} max={progress.total} />
              <span className="text-xs text-dim">
                {progress.running
                  ? t('playlist.enriching', {
                      resolved: progress.resolved,
                      total: progress.total,
                    })
                  : t('playlist.enrichDone')}
              </span>
              {progress.running && (
                <span className="text-xs text-dim">{t('playlist.enrichHint')}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- Beliebiger Spotify-Link --- */}
      <div className="field" style={{ marginBottom: '1rem' }}>
        <label className="field__label" htmlFor="playlist-url">
          {t('playlist.paste')}
        </label>
        <div className="row" style={{ gap: '0.5rem' }}>
          <input
            id="playlist-url"
            className="input"
            style={{ flex: 1, minWidth: 0 }}
            placeholder={t('playlist.pastePlaceholder')}
            value={pasted}
            onChange={(event) => setPasted(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && pasted.trim()) void select(pasted.trim());
            }}
          />
          <button
            type="button"
            className="btn btn--primary"
            disabled={pasted.trim().length === 0 || busyRef !== null}
            onClick={() => void select(pasted.trim())}
          >
            {busyRef === pasted.trim() ? <Spinner /> : t('playlist.load')}
          </button>
        </div>
      </div>

      {/* --- Eigene Playlists --- */}
      <div className="row row--between" style={{ marginBottom: '0.5rem' }}>
        <span className="field__label">{t('playlist.yourPlaylists')}</span>
        {/* Erst nach dem ersten Laden gibt es etwas zu aktualisieren. */}
        {playlists !== null && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => loadPlaylists(true)}
            disabled={loadingList}
            title={t('playlist.refresh')}
            aria-label={t('playlist.refresh')}
          >
            {loadingList ? <Spinner /> : <IconReplay />}
          </button>
        )}
      </div>

      {listError && <Alert kind="error">{t(`error.${listError}` as 'error.internal_error')}</Alert>}

      {/* Das Auflisten kostet bis zu zehn Anfragen -- deshalb auf Ansage. */}
      {playlists === null && (
        <div className="stack stack--tight">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => loadPlaylists(false)}
            disabled={loadingList}
          >
            {loadingList ? <Spinner /> : t('playlist.showMine')}
          </button>
          <span className="text-xs text-dim">{t('playlist.showMineHint')}</span>
        </div>
      )}

      {playlists !== null && playlists.length === 0 && !loadingList && (
        <p className="text-sm text-dim">{t('playlist.empty')}</p>
      )}

      {/*
       * Die Liste enthält nur eigene Playlists -- der Server filtert
       * fremde heraus, weil Spotify deren Inhalt für Apps im
       * Development Mode sperrt. Ohne diesen Hinweis sucht der Host
       * vergeblich nach den Listen, denen er folgt.
       */}
      {playlists !== null && playlists.length > 0 && (
        <p className="text-xs text-dim" style={{ marginBottom: '0.5rem' }}>
          {t('playlist.ownOnly')}
        </p>
      )}

      <div className="playlist-list">
        {(playlists ?? []).map((playlist) => {
          return (
            <button
              key={playlist.id}
              type="button"
              className={`playlist-item ${selected?.id === playlist.id ? 'playlist-item--selected' : ''}`}
              disabled={busyRef !== null}
              onClick={() => void select(playlist.id)}
            >
              {playlist.imageUrl ? (
                <img
                  className="playlist-item__cover"
                  src={playlist.imageUrl}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <div className="playlist-item__cover" />
              )}
              <div className="min-w-0" style={{ flex: 1 }}>
                <div className="playlist-item__name">{playlist.name}</div>
                <div className="playlist-item__meta">
                  {/* Spotify liefert die Songzahl nicht immer mit. Dann '?'
                      anzeigen statt einer erfundenen 0. */}
                  {t('playlist.tracks', { count: playlist.trackCount ?? '?' })} ·{' '}
                  {playlist.ownerName}
                </div>
              </div>
              {busyRef === playlist.id && <Spinner />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
